import {useCallback, useEffect, useMemo, useState} from 'react'
import {useRouteMatch} from 'react-router-dom'
import {useAlerts} from '../../../../components/alerts/useAlerts'
import {useBreakpoint} from '../../../../components/hooks/useBreakpoint'
import {MetronicIconButton} from '../../../../components/inputs/MetronicIconButton'
import {SweetAlert} from '../../../../components/modals/SweetAlert'
import {HorizontalDivider} from '../../../../components/utils/HorizontalDivider'
import {QrCode} from '../../../../components/utils/QrCode'
import {TicketPortalModel} from '../../../../models/ems/TicketModel'
import {useDay} from '../../hooks/useDay'
import {useIsGSSSPP} from '../../hooks/useIsGSSSPP'
import {RouteParams} from '../../pages/PortalPage'
import {GetWalletQrCode} from '../../redux/CustomerPortalCRUD'

interface WalletModalProps {
  open: boolean
  setOpen: (value: boolean) => void
  data: TicketPortalModel[]
  onSubmit: (data: TicketPortalModel) => any
}

export interface InitialValuesProps {
  email: string
}

export const WalletModal = ({open, setOpen, data, onSubmit}: WalletModalProps) => {
  const {pushError} = useAlerts()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const {
    params: {productCategoryCode},
  } = useRouteMatch<RouteParams>()

  const {getDayThreeLetters} = useDay()

  const handleClose = useCallback(() => {
    setOpen(false)
  }, [setOpen])
  const {isGCCSPP} = useIsGSSSPP({productCategoryCode})

  const {down} = useBreakpoint()

  const isMobile = useMemo(() => {
    return down('xs')
  }, [down])

  const [qrCodeURL, setQrCodeURL] = useState<string>()

  const handleQrCode = useCallback(async () => {
    try {
      if (data && data.length > 0) {
        const {data: response} = await GetWalletQrCode(data[0]?.code)

        setQrCodeURL(response.qrCode)
      }
    } catch (err) {
      pushError(err)
    }
  }, [data, pushError])

  useEffect(() => {
    handleQrCode()
  }, [handleQrCode])

  const handleSubmit = useCallback(async () => {
    if (data) {
      setIsLoading(true)
      const response = await onSubmit(data[0])
      if (response) {
        setIsLoading(false)
      }
    }
  }, [data, onSubmit])

  const day = useMemo(() => {
    return getDayThreeLetters(data[0].startedAt, data[0].endedAt)
  }, [data, getDayThreeLetters])

  return (
    <SweetAlert
      customClass={{popup: 'bg-primary-cp-dark'}}
      open={open}
      onClose={handleClose}
      showConfirmButton={false}
      width='auto'
    >
      <div className='px-sm-0 px-md-5'>
        <div className='d-flex px-5'>
          <div className='flex-grow-1 text-start d-flex flex-column justify-content-center'>
            <p className='text-white ff-admm-bold fs-1 mb-0'>Wallet</p>
          </div>
          <div>
            <MetronicIconButton
              type='button'
              className='btn'
              iconType='Navigation'
              iconName='Close'
              variant='text'
              size='md'
              color='white'
              onClick={handleClose}
            />
          </div>
        </div>
        <HorizontalDivider className='bg-dark' />
        <div className='d-flex gap-10 mt-5 px-5'>
          <div>{qrCodeURL && <QrCode size={isMobile ? 125 : 200} value={qrCodeURL} />}</div>
          <div className='d-flex flex-column justify-content-center'>
            <div className='flex-grow-1'>
              <p
                className='text-white text-start ff-admm-bold mb-0 pb-2'
                style={{
                  fontSize: isMobile ? '15px' : '20px',
                }}
              >
                {!isGCCSPP && day === 'Thu' ? 'Pitlane Walk' : data[0].productName}
              </p>
              {day !== 'Thu' && data[0].seatRow && data[0].seatNo && (
                <div
                  className='text-white text-start ff-admm-bold mb-0 pb-2 d-flex gap-3 flex-wrap'
                  style={{
                    fontSize: isMobile ? '15px' : '20px',
                  }}
                >
                  <div> {`Section: ${data[0].locationSlug}`}</div>
                  <div> {`Row: ${data[0].seatRow}`}</div>
                  <div> {`Seat: ${data[0].seatNo}`}</div>
                </div>
              )}
            </div>
            {!isMobile && (
              <div className='flex-grow-1 d-flex flex-column justify-content-end'>
                <button
                  className='btn bg-body d-flex gap-2 mt-2 rounded-0 text-white justify-content-center'
                  onClick={handleSubmit}
                >
                  Add To Wallet
                  {isLoading && (
                    <span className='indicator-progress' style={{display: 'block'}}>
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
        {isMobile && (
          <div className='w-100'>
            <button
              className='btn bg-body d-flex gap-2 mx-auto mt-5 rounded-0 text-white w-75 justify-content-center'
              onClick={handleSubmit}
            >
              Add To Wallet
              {isLoading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        )}
      </div>
    </SweetAlert>
  )
}
